import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  Calendar,
  Copy,
  FileCheck2,
  Inbox,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/hooks/useConfirm';
import api from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { PageIntro } from '@/components/layout/PageIntro';
import AidaHero from '@/components/brand/AidaHero';
import { AidaFormsLogo } from '@/components/ui/LuminaLogo';

interface FormTemplate {
  id: string;
  name: string;
  description: string;
  formName: string;
  formTitle: string;
  fieldsCount: number;
  createdAt: string;
  updatedAt: string;
}

const FormsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const tt = (key: string, fallback: string, options?: Record<string, unknown>) =>
    t(key, { defaultValue: fallback, ...options });
  const { confirm } = useConfirm();

  const [forms, setForms] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/flow-engine/form-templates');
      if (response.data.success) {
        setForms(response.data.formTemplates);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      toast.error(tt('formsList.toasts.loadError', 'Error loading forms'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: tt('formsList.confirm.deleteTitle', 'Delete form'),
      message: tt(
        'formsList.confirm.deleteMessage',
        'Are you sure you want to delete "{{name}}"?',
        { name },
      ),
      confirmText: tt('formsList.actions.delete', 'Delete'),
      type: 'danger',
    });
    if (!confirmed) return;

    try {
      const response = await api.delete(`/flow-engine/form-templates/${id}`);
      if (response.data.success) {
        toast.success(tt('formsList.toasts.deleteSuccess', 'Form deleted successfully'));
        loadForms();
      }
    } catch (error) {
      console.error('Error deleting form:', error);
      toast.error(tt('formsList.toasts.deleteError', 'Error deleting form'));
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const response = await api.get(`/flow-engine/form-templates/${id}`);
      if (response.data.success) {
        const template = response.data.formTemplate;
        navigate('/forms/new', {
          state: {
            ...template,
            formName: `${template.formName}_copy`,
            formTitle: `${template.formTitle} (${tt('formsList.actions.copySuffix', 'Copy')})`,
          },
        });
      }
    } catch (error) {
      console.error('Error duplicating form:', error);
      toast.error(tt('formsList.toasts.duplicateError', 'Error duplicating form'));
    }
  };

  const getFormUrl = (formName: string) =>
    `${window.location.origin}/form/${formName}`;

  const copyFormUrl = (formName: string) => {
    const url = getFormUrl(formName);
    navigator.clipboard.writeText(url);
    toast.success(tt('formsList.toasts.urlCopied', 'URL copied'));
  };

  const filteredForms = useMemo(
    () =>
      forms.filter(
        (form) =>
          form.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          form.formName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          form.formTitle?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [forms, searchTerm],
  );

  const dateLocale =
    i18n.language === 'pt-BR' ? 'pt-BR' : i18n.language === 'es' ? 'es-ES' : 'en-US';
  const hasSearch = searchTerm.trim().length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 text-brand-500 animate-spin" />
      </div>
    );
  }

  if (forms.length === 0) {
    return (
      <div className="space-y-5">
        <PageIntro
          className="border-transparent"
          eyebrow={
            <>
              <Sparkles className="h-3.5 w-3.5" />
              {tt('formsList.badge', 'AIDA Forms')}
            </>
          }
          title={tt('formsList.title', 'Formulários')}
          description={tt(
            'formsList.description',
            'Crie formulários elegantes com editor centralizado, preview ao vivo e uma experiência pública mais limpa.',
          )}
          actions={
            <div className="flex w-full flex-col gap-2 lg:w-[760px]">
              <div className="flex justify-end">
                <Button
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => navigate('/forms/new')}
                >
                  {tt('formsList.newForm', 'Criar formulário')}
                </Button>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={tt(
                    'formsList.searchPlaceholder',
                    'Buscar por título, slug ou descrição...',
                  )}
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 !pl-9 !pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          }
          footer={
            <>
              <span>
                <strong className="text-slate-900 dark:text-white">0</strong>{' '}
                {tt('formsList.counts.forms', 'formulários')}
              </span>
            </>
          }
        />

        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <Inbox className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {tt('formsList.empty.title', 'Nenhum formulário criado')}
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-slate-500 dark:text-gray-400">
            {tt(
              'formsList.empty.description',
              'Crie seu primeiro formulário para começar a coletar respostas com um layout padronizado.',
            )}
          </p>
          <div className="mt-6">
            <Button
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/forms/new')}
            >
              {tt('formsList.newForm', 'Criar formulário')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 px-2.5 py-2.5">
      <AidaHero />

      <div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative lg:max-w-[760px] lg:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={tt(
              'formsList.searchPlaceholder',
              'Buscar por título, slug ou descrição...',
            )}
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 !pl-9 !pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex items-center justify-end gap-2 text-sm text-slate-500 dark:text-gray-400">
          <span>
            <strong className="text-slate-900 dark:text-white">
              {forms.length}
            </strong>{' '}
            {forms.length === 1
              ? tt('formsList.counts.form', 'formulário')
              : tt('formsList.counts.forms', 'formulários')}
          </span>
          {hasSearch && (
            <>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span>
                <strong className="text-brand-600 dark:text-brand-400">
                  {filteredForms.length}
                </strong>{' '}
                {tt('formsList.counts.visible', 'visíveis')}
              </span>
            </>
          )}
          <Button
            icon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/forms/new')}
            className="ml-2 bg-[#e20074] text-white hover:bg-[#c70065] focus:ring-[#e20074]/40"
          >
            {tt('formsList.newForm', 'Criar formulário')}
          </Button>
        </div>
      </div>

      {filteredForms.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-6 py-20 text-center dark:border-gray-700 dark:bg-gray-800/70">
          <Inbox className="mx-auto mb-3 h-12 w-12 text-slate-300 dark:text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {tt('formsList.empty.searchTitle', 'Nenhum formulário encontrado')}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">
            {tt('formsList.empty.searchDescription', 'Tente buscar com termos diferentes.')}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredForms.map((form) => (
            <article
              key={form.id}
              className="group overflow-hidden rounded-[22px] border border-slate-200/80 bg-white shadow-[0_10px_24px_rgba(40,36,46,0.05)] transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_18px_34px_rgba(40,36,46,0.08)] dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="border-b border-slate-100 bg-[linear-gradient(135deg,#fdf0f7_0%,#fff5fa_100%)] px-4 py-3 rounded-t-[22px] dark:border-gray-700 dark:bg-gray-800/70 dark:[background-image:none]">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#e20074_0%,#f185bc_100%)] text-white shadow-[0_10px_20px_rgba(226,0,116,0.22)]">
                      <FileCheck2 className="h-5 w-5 text-white/90" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                          {tt('formsList.status.ready', 'Pronto')}
                        </span>
                      </div>
                      <h2 className="truncate text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                        {form.formTitle}
                      </h2>
                      <p className="mt-0.5 truncate text-[11px] font-mono text-slate-400 dark:text-gray-500">
                        /form/{form.formName}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => window.open(getFormUrl(form.formName), '_blank')}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
                  >
                    <ArrowUpRight className="h-3.5 w-3.5" />
                    {tt('formsList.actions.openLive', 'Abrir público')}
                  </button>
                </div>
              </div>

              <div className="p-4 rounded-b-[22px]">
                <p className="line-clamp-2 text-[13px] leading-5 text-slate-500 dark:text-gray-400">
                  {form.description ||
                    tt('formsList.noDescription', 'Sem descrição definida.')}
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400 dark:text-gray-500">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] dark:bg-gray-700/70 inline-flex items-center gap-1">
                    <ListChecks className="h-3.5 w-3.5" />
                    {tt('formsList.metrics.fields', 'Campos')}: {form.fieldsCount}
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] dark:bg-gray-700/70 inline-flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {tt('formsList.metrics.updated', 'Atualizado')}:{' '}
                    {new Date(form.updatedAt).toLocaleDateString(dateLocale)}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 truncate rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                    /form/{form.formName}
                  </code>
                  <button
                    onClick={() => copyFormUrl(form.formName)}
                    className="rounded-xl border border-slate-200 p-2.5 text-slate-500 transition hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-brand-500/40 dark:hover:text-brand-300"
                    title={tt('formsList.actions.copyUrl', 'Copiar URL')}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => navigate(`/forms/${form.id}/edit`)}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#e20074] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#c70065]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {tt('formsList.actions.edit', 'Editar')}
                  </button>
                  <button
                    onClick={() => handleDuplicate(form.id)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-brand-300 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-brand-500/40 dark:hover:text-brand-300"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    {tt('formsList.actions.duplicate', 'Duplicar')}
                  </button>
                  <button
                    onClick={() => handleDelete(form.id, form.name)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-red-300 hover:text-red-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-red-500/40 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {tt('formsList.actions.delete', 'Excluir')}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default FormsListPage;
